/**
 * ToFinchSuggestionCard - Card for TO_FINCH direction suggestions
 *
 * Shows a suggestion to pull data from Filevine to Finch.
 * Features:
 * - Entity pair display (Filevine → Finch)
 * - Simple field changes display (name, notes, status, care_level)
 * - Accept/Reject actions
 * - Edit modal with MedicalProviderEntityDetails
 */

'use client';

import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowDownLeft,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import React, { forwardRef } from 'react';

import {
  CarrierDivisionLink,
  ProviderServiceLink,
} from '@/components/entity-links';
import { cn } from '@/lib/utils';

import { formatEnumValue } from '../../shared/EntityFieldComponents';
import {
  COVERAGE_FIELD_CONFIGS,
  getFieldLabels,
  MEDICAL_PROVIDER_FIELD_CONFIGS,
} from '../../shared/fieldConfigs';
import { FilevineEntityCard } from '../FilevineEntityCard';
import { FinchEntityCard } from '../finch-cards';
import {
  ConfidenceBadge,
  SuggestionCardActions,
} from './SuggestionCardShared';
import { SuggestionSetDetailsSheet } from './SuggestionSetDetailsSheet';
import type { ToFinchSuggestionCardProps } from './types';
import { useToFinchSuggestionCard } from './useToFinchSuggestionCard';

// ============================================================================
// Helpers
// ============================================================================

/** Field labels for Finch entity payloads, derived from centralized configs */
const FIELD_LABELS_BY_TYPE: Record<string, Record<string, string>> = {
  medical_providers: getFieldLabels(MEDICAL_PROVIDER_FIELD_CONFIGS),
  coverage: getFieldLabels(COVERAGE_FIELD_CONFIGS),
};

// ============================================================================
// Types
// ============================================================================

interface FieldChange {
  field: string;
  label: string;
  newValue: string | null | undefined;
  currentValue: string | null | undefined;
}

// ============================================================================
// Sub-components
// ============================================================================

/** Fields that are directory IDs and need entity link rendering */
const DIRECTORY_ID_FIELDS = new Set([
  'provider_service_id',
  'carrier_division_id',
]);

/** Render a field value, using entity link components for directory ID fields */
function renderFieldValue(
  field: string,
  value: string | null | undefined,
  fallback: string,
): React.ReactNode {
  if (!value) return fallback;

  if (field === 'provider_service_id') {
    return (
      <ProviderServiceLink entityId={value} className="text-sm">
        Provider Directory
      </ProviderServiceLink>
    );
  }

  if (field === 'carrier_division_id') {
    return (
      <CarrierDivisionLink entityId={value} className="text-sm">
        Carrier Directory
      </CarrierDivisionLink>
    );
  }

  return formatEnumValue(value);
}

const FieldChangeRow: React.FC<{
  change: FieldChange;
}> = ({ change }) => {
  const isDirectoryField = DIRECTORY_ID_FIELDS.has(change.field);

  return (
    <tr className="transition-colors bg-white">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <ArrowDownLeft className="w-4 h-4 text-emerald-500" />
          <span className="font-medium text-zinc-700">{change.label}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span
          className={cn(
            'text-zinc-600',
            change.newValue && change.newValue !== change.currentValue
              ? 'opacity-50 line-through'
              : '',
          )}
        >
          {isDirectoryField
            ? renderFieldValue(change.field, change.currentValue, '(none)')
            : change.currentValue
              ? formatEnumValue(change.currentValue)
              : '(none)'}
        </span>
      </td>
      <td className="px-2 py-3 text-center">
        <ArrowLeft className="w-4 h-4 text-zinc-300 inline-block" />
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-zinc-700">
          {renderFieldValue(change.field, change.newValue, '(clear)')}
        </span>
      </td>
    </tr>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const ToFinchSuggestionCard = forwardRef<
  HTMLDivElement,
  ToFinchSuggestionCardProps
>(function ToFinchSuggestionCard(
  {
    suggestion,
    parentSet,
    finchEntity,
    filevineEntity,
    fieldConfigs,
    sectionConfigData,
    canonicalSectionType,
    matterId,
    onAccept,
    onReject,
    index,
  },
  ref,
) {
  const card = useToFinchSuggestionCard({
    suggestion,
    parentSet,
    onAccept,
    onReject,
  });

  const isCreate = suggestion.action_type === 'CREATE';
  const withReview = suggestion as typeof suggestion & {
    is_auto_pushed?: boolean;
    review_status?: string | null;
  };
  const isAutoPushReview =
    withReview.is_auto_pushed === true &&
    suggestion.status === 'COMPLETED' &&
    withReview.review_status === 'PENDING_REVIEW';

  // Get field labels for the current entity type
  const fieldLabels = FIELD_LABELS_BY_TYPE[canonicalSectionType] ?? {};

  // Extract field changes from the payload - only show fields that are actually changing
  const fieldChanges: FieldChange[] = React.useMemo(() => {
    if (!card.editedPayload) return [];

    const changes: FieldChange[] = [];
    const payload = card.editedPayload;

    // Get current values from finchEntity
    const currentEntity = finchEntity as Record<string, unknown> | null;

    // Check each field in the payload for actual changes
    // Only consider fields that are present in the payload — fields absent from
    // the payload are NOT being changed and should not show as "(clear)".
    Object.keys(fieldLabels).forEach((field) => {
      if (!(field in payload)) return;

      const newValue = payload[field];
      const currentValue = currentEntity?.[field];

      // Normalize values for comparison (treat null, undefined, and empty string as equivalent)
      const normalizedNew = (newValue as string) ?? '';
      const normalizedCurrent = (currentValue as string) ?? '';

      // Only show fields where the value is actually different
      if (normalizedNew !== normalizedCurrent) {
        changes.push({
          field,
          label: fieldLabels[field],
          newValue: newValue as string | null | undefined,
          currentValue: currentValue as string | null | undefined,
        });
      }
    });

    return changes;
  }, [card.editedPayload, finchEntity, fieldLabels]);

  // Determine status-based styling
  const isPending = suggestion.status === 'PENDING';
  const isProcessing =
    suggestion.status === 'ACCEPTED' || suggestion.status === 'EXECUTING';
  const isCompleted = suggestion.status === 'COMPLETED';
  const isFailed = suggestion.status === 'FAILED';

  // Accent line color based on status
  const accentLineClass = cn(
    'absolute top-0 left-0 right-0 h-1 bg-gradient-to-r',
    isPending && 'from-emerald-500 to-teal-500',
    isProcessing && 'from-amber-400 to-amber-500',
    isCompleted && 'from-emerald-500 to-teal-500',
    isFailed && 'from-rose-500 to-red-500',
  );

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      className="group relative"
    >
      <div
        className={cn(
          'relative bg-white rounded-2xl border shadow-sm transition-all duration-200 overflow-hidden',
          isPending && 'border-zinc-200 hover:shadow-md',
          isProcessing && 'border-amber-200 bg-amber-50/30',
          isCompleted && 'border-emerald-200 bg-emerald-50/30',
          isFailed && 'border-rose-200 bg-rose-50/30',
        )}
      >
        {/* Accent line - color changes based on status */}
        <div className={accentLineClass} />

        {/* Entity Cards Section */}
        <div className="p-4 pt-5">
          <div className="flex items-stretch gap-4">
            {/* Finch Entity Card (destination) */}
            <div className="flex-1 min-w-0 flex">
              {finchEntity ? (
                <FinchEntityCard
                  entity={finchEntity}
                  canonicalSectionType={canonicalSectionType}
                  matterId={matterId}
                  compact
                  className="flex-1"
                />
              ) : (
                <div className="flex-1 rounded-lg border border-dashed border-zinc-200 p-3 text-center text-sm text-zinc-400 flex items-center justify-center">
                  Will create in Finch
                </div>
              )}
            </div>

            {/* Direction indicator */}
            <div className="flex flex-col items-center justify-center gap-1">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100">
                <ArrowDownLeft className="h-4 w-4 text-emerald-600" />
              </div>
              <span className="text-xs font-medium text-emerald-600">
                {finchEntity ? 'Update' : 'Create'}
              </span>
            </div>

            {/* Filevine Entity Card (source) */}
            <div className="flex-1 min-w-0 flex">
              {filevineEntity ? (
                <FilevineEntityCard
                  entity={filevineEntity}
                  fieldConfigs={fieldConfigs}
                  sectionConfigData={sectionConfigData}
                  compact
                  className="flex-1"
                />
              ) : (
                <div className="flex-1 rounded-lg border border-dashed border-zinc-200 p-3 text-center text-sm text-zinc-400 flex items-center justify-center">
                  No Filevine entity
                </div>
              )}
            </div>

            {/* Confidence badge */}
            <ConfidenceBadge
              confidence={card.confidence}
              aiReasoning={card.aiReasoning}
            />
          </div>
        </div>

        {/* Changes section */}
        <div className="border-t border-zinc-100">
          <button
            type="button"
            onClick={card.toggleExpand}
            className="w-full px-5 py-3 flex items-center justify-between text-sm font-medium text-zinc-600 hover:bg-zinc-50 transition-colors"
          >
            {card.isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          <AnimatePresence>
            {card.isExpanded && fieldChanges.length > 0 && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="px-5 pb-4">
                  <div className="rounded-xl border border-zinc-200 overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-zinc-50 text-left">
                          <th className="px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                            Field
                          </th>
                          <th className="px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                            Current
                          </th>
                          <th className="w-10 px-2 py-2.5" />
                          <th className="px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                            New
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {fieldChanges.map((change) => (
                          <FieldChangeRow key={change.field} change={change} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Empty state */}
          {card.isExpanded && fieldChanges.length === 0 && (
            <div className="px-5 pb-4">
              <div className="rounded-xl border border-zinc-200 p-4 text-center text-sm text-zinc-500">
                {isCreate
                  ? 'Create new entity in Finch'
                  : 'Update entity in Finch'}
              </div>
            </div>
          )}
        </div>

        {/* Actions - changes based on suggestion status */}
        <SuggestionCardActions
          status={suggestion.status}
          isCreate={isCreate}
          isAutoPushReview={isAutoPushReview}
          autoPushReviewStatus={
            withReview.is_auto_pushed ? withReview.review_status : null
          }
          hasEdits={card.isPayloadModified}
          destinationLabel="Finch"
          accentColor="emerald"
          card={card}
        />
      </div>

      {/* Edit Modal - reuses SuggestionSetDetailsSheet for comparison view */}
      <SuggestionSetDetailsSheet
        suggestionSet={parentSet}
        open={card.isEditModalOpen}
        onOpenChange={card.setEditModalOpen}
        finchEntity={finchEntity}
        filevineEntity={filevineEntity}
        fieldConfigs={fieldConfigs}
        finchEditable
        onFinchPayloadChange={card.updateField}
        modifiedFinchPayload={card.editedPayload ?? undefined}
        direction="TO_FINCH"
      />
    </motion.div>
  );
});

export default ToFinchSuggestionCard;
